import React, {ComponentProps, useMemo} from "react";
import {gql} from "@apollo/client";
import {Cell, Column, useExpanded, useTable} from "react-table";
import {Challenge, ChallengeProject} from "../../../schema";
import {useTranslation} from "react-i18next";
import ChallengeEditDropdown from "./ChallengeEditDropdown";
import ChallengeTableItem from "./ChallengeTableItem";
import LanguageIcon from "../../../ui/languageicon";
import {formatRelativeDateTime} from "../../../time";
import classes from "./ChallengesTable.module.css";
import ProjectEditDropdown from "./ProjectEditDropdown";
import ProjectTableItem from "./ProjectTableItem";

type CellDataType = ChallengeProject;

type ChallengeDropdownProps = ComponentProps<typeof ChallengeEditDropdown>;
type ProjectDropdownProps = ComponentProps<typeof ProjectEditDropdown>

type GenericParams = {
  projects: ChallengeDropdownProps["projects"],
  currentProject?: ChallengeDropdownProps["currentProject"],
  onChallengeCopy: ChallengeDropdownProps["onCopy"];
  onChallengeDelete: ChallengeDropdownProps["onDelete"];
  onChallengeMove: ChallengeDropdownProps["onMove"],
  onSubscriptionModalOpen?: () => void,
};

export type ChallengeParams = GenericParams & {
  data: Challenge[];
  onProjectCopy?: undefined,
  onProjectDelete?: undefined,
  optionsDisabled?: boolean,
  manageDisabled?: boolean
};

export type ProjectParams = GenericParams & {
  data: ChallengeProject[],
  optionsDisabled?: boolean,
  onProjectCopy: ProjectDropdownProps["onCopy"],
  onProjectDelete: ProjectDropdownProps["onDelete"]
}

export type Params = ChallengeParams | ProjectParams;

function useChallengesTableTable(
  data: Params["data"],

  projects: Params["projects"],
  currentProject: Params["currentProject"],
  optionsDisabled: Params["optionsDisabled"],
  onChallengeCopy: Params["onChallengeCopy"],
  onChallengeDelete: Params["onChallengeDelete"],
  onChallengeMove: Params["onChallengeMove"],
  onProjectCopy: Params["onProjectCopy"],
  onProjectDelete: Params["onProjectDelete"],
  onSubscriptionModalOpen: Params["onSubscriptionModalOpen"]
) {
  const {t, i18n} = useTranslation();

  const columns = useMemo<Column<CellDataType>[]>(
    () => [
      {
        id: "challenge",
        Header: () => t("components.ChallengesTable.challenge"),
        Cell: ({row}: Cell<CellDataType>) => {
        switch (row.original.__typename) {
          case "Challenge":
            return (
              <ChallengeTableItem
                challenge={row.original}
              />
            )
          case "Project":
            return (
              <ProjectTableItem
                project={row.original}
              />
            )
          default:
            console.error(`Unsupported data type "${row.original.__typename}" in challenge (name) column`)
            return null;
        }
      },
      },

      {
        id: "language",
        Header: () => t("components.ChallengesTable.language"),
        Cell: ({row}: Cell<CellDataType>) => {
        switch (row.original.__typename) {
          case "Challenge":
          case "Project":
            return (
              <span>
                <LanguageIcon
                  value={row.original.languageCode}
                  width={29}
                  height={16}
                />
              </span>
            )
          default:
            console.error(`Unsupported data type "${row.original.__typename}" in language column`)
            return null;
        }
      }
      },

      {
        id: "lastUpdatedAt",
        Header: () => t("components.ChallengesTable.lastUpdatedAt"),
        Cell: ({row}: Cell<CellDataType>) => {
        switch (row.original.__typename) {
          case "Challenge":
          case "Project":
            return (
              <span className={classes.dateTime}>
                {formatRelativeDateTime(row.original.lastUpdatedAt, i18n.language)}
              </span>
            )
          default:
            console.error(`Unsupported data type "${row.original.__typename}" in lastUpdatedAt column`)
            return null;
        }
      }
      },

      {
        id: "options",
        Header: () => null,
        Cell: ({row}: Cell<CellDataType>) => {
          switch (row.original.__typename) {
            case "Challenge":
              return (
                <ChallengeEditDropdown
                  challenge={row.original}
                  currentProject={currentProject}
                  projects={projects}
                  onCopy={onChallengeCopy}
                  onDelete={onChallengeDelete}
                  onMove={onChallengeMove}
                  disabled={optionsDisabled}
                />
              )
            case "Project":
              return (
                <ProjectEditDropdown
                  project={row.original}
                  onCopy={onProjectCopy!}
                  onDelete={onProjectDelete!}
                  disabled={optionsDisabled}
                />
              )
            default:
              console.error(`Unsupported data type "${row.original.__typename}" in lastUpdatedAt column`)
              return null;
          }
        },
      },
    ],
    [t, i18n.language, currentProject, projects, onChallengeCopy,
      onChallengeDelete, onChallengeMove, optionsDisabled, onProjectCopy, onProjectDelete],
  );

  return useTable<CellDataType>(
    {
      columns: columns,
      data: data,
    },
    useExpanded,
  );
}

const ChallengesTableTable = {
  use: useChallengesTableTable,
  fragments: {
    root: gql`
      fragment ChallengesTableTable on ChallengeProject {
        __typename

        ... on Challenge {
          id
          ...ChallengesTableTableChallenge
        }

        ... on Project {
          id
          ...ChallengesTableTableProject
        }
      }

      fragment ChallengesTableTableProject on Project {
        name
        languageCode
        # createdAt
        lastUpdatedAt
        challenges {
          id
          ...ChallengesTableTableChallenge
        }
      }

      fragment ChallengesTableTableChallenge on Challenge {
        projectId
        projectName
        languageCode
        lastUpdatedAt
        ...ChallengeTableItem
        ...ChallengeEditDropdown
      }
      ${ChallengeTableItem.fragments.root}
      ${ChallengeEditDropdown.fragments.root}
    `,
    projects: gql`
      fragment ChallengesTableTableProjects on Project {
        ...ChallengeEditDropdownProjects
      }
      ${ChallengeEditDropdown.fragments.projects}
    `
  },
};

export default ChallengesTableTable;
